/**
 * JSON members and nested JSON for `redactTargetSecrets` (split out of `secretTextScanners.ts`). Linear: every unescaped
 * quote is tried once as a string's opening quote; each step moves to that string's closing quote.
 */
import { isSpace, type Span } from './secretTextScanners';

/** Where the balanced `{…}` / `[…]` starting at `start` ends (string-aware), or -1 when it never closes. Linear. */
export function jsonValueEnd(text: string, start: number): number {
  const closers: string[] = [];
  let inString = false;
  for (let i = start; i < text.length; i += 1) {
    const c = text[i];
    if (inString) {
      if (c === '\\') i += 1;
      else if (c === '"') inString = false;
    } else if (c === '"') {
      inString = true;
    } else if (c === '{' || c === '[') {
      closers.push(c === '{' ? '}' : ']');
    } else if (c === '}' || c === ']') {
      if (closers.pop() !== c) return -1;
      if (closers.length === 0) return i + 1;
    }
  }
  return -1;
}

/** The JSON string starting at `open` (a `"`): index of its closing quote, or -1 when it never closes. */
export function jsonStringEnd(text: string, open: number): number {
  for (let i = open + 1; i < text.length; i += 1) {
    if (text[i] === '\\') i += 1;
    else if (text[i] === '"') return i;
  }
  return -1;
}

const WALKER_LABEL = /^"<redacted(?::\w+ \d+chars)?>"$/u;
const MAX_JSON_KEY = 256;

/** The next `"` at or after `from` that is not escaped by a backslash (-1). */
function nextOpenQuote(text: string, from: number): number {
  for (let i = text.indexOf('"', from); i >= 0; i = text.indexOf('"', i + 1)) {
    if (text[i - 1] !== '\\') return i;
  }
  return -1;
}

/**
 * A JSON member whose (decoded) name is a secret name, `"password": "…"` or `"pass\u0077ord": …`: its string content
 * (to the end of the text when the string never closes), or its bare value. Every unescaped quote is tried as a key's
 * opening quote, so a stray quote before a member cannot shift the reading; each step moves to the string's closing
 * quote, so the walk is linear. A value that is exactly the backend log's JSON-walker label is left as it is.
 */
export function jsonMemberSpans(
  text: string,
  isSecretName: (key: string) => boolean,
  spans: Span[],
  nestedHasSecret: (decoded: string) => boolean = () => false,
): void {
  const n = text.length;
  let i = nextOpenQuote(text, 0);
  // The next backslash, advanced monotonically, so checking every string for an escape stays linear. Any JSON escape
  // can hide a nested document's quotes — `\"` or `\u0022` (.NET's default encoder; review round 2, Codex #6).
  let escaped = text.indexOf('\\');
  while (i >= 0) {
    const close = jsonStringEnd(text, i);
    // No string closes after here (a later quote would scan the same characters): nothing more to find.
    if (close < 0) return;
    if (escaped >= 0 && escaped <= i) escaped = text.indexOf('\\', i + 1);
    if (escaped > i && escaped < close) maskNestedJson(text, i, close, spans, nestedHasSecret);
    let k = close + 1;
    while (k < n && isSpace(text[k])) k += 1;
    if (close - i > MAX_JSON_KEY || text[k] !== ':' || !isSecretName(jsonKey(text, i, close))) {
      i = close;
      continue;
    }
    k += 1;
    while (k < n && isSpace(text[k])) k += 1;
    if (text[k] === '"') {
      const end = jsonStringEnd(text, k);
      if (end < 0) { spans.push([k + 1, n]); return; }
      if (!WALKER_LABEL.test(text.slice(k, end + 1))) spans.push([k + 1, end]);
      i = nextOpenQuote(text, end + 1);
    } else if (text[k] === '{' || text[k] === '[') {
      // An object or array value is masked whole, to its balanced end — to the end of the text when it never closes
      // (review round 4, Codex R4-5 / Fable N4: the value used to end at its first comma or space).
      const end = jsonValueEnd(text, k);
      if (end < 0) { spans.push([k, n]); return; }
      spans.push([k, end]);
      i = nextOpenQuote(text, end);
    } else {
      let valueEnd = k;
      while (valueEnd < n && !',}]'.includes(text[valueEnd]) && !isSpace(text[valueEnd])) valueEnd += 1;
      spans.push([k, valueEnd]);
      i = nextOpenQuote(text, valueEnd);
    }
  }
}

/** A string holding any escape is decoded and checked one level down; any secret there masks it whole. */
function maskNestedJson(text: string, open: number, close: number, spans: Span[], nestedHasSecret: (decoded: string) => boolean): void {
  let decoded: string;
  try {
    decoded = JSON.parse(text.slice(open, close + 1)) as string;
  } catch {
    return;
  }
  if (typeof decoded === 'string' && nestedHasSecret(decoded)) spans.push([open + 1, close]);
}

function jsonKey(text: string, open: number, close: number): string {
  try {
    return JSON.parse(text.slice(open, close + 1)) as string;
  } catch {
    return text.slice(open + 1, close);
  }
}
